'use client'

import {
  addListener,
  createAsyncThunk,
  createListenerMiddleware,
  createSelector,
  TypedStartListening,
} from '@reduxjs/toolkit'
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query'
import {
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { disconnect } from '@wagmi/core'
import toast from 'react-hot-toast'
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux'

import { TOKEN } from '@/constants/local-storage'
import {
  getBaseUrl,
  getEnv,
} from '@/lib/env'
import { getWagmiConfig } from '@/lib/wagmi-config'
import { setToken } from '@/redux/global-actions'

import type {
  AppDispatch,
  RootState,
} from './index'

export type AppStartListening = TypedStartListening<RootState, AppDispatch>;
export type AppSelector = TypedUseSelectorHook<RootState>;
export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const appListener = createListenerMiddleware()

export const startAppListening = appListener.startListening.withTypes<
  RootState,
  AppDispatch
>()
export const addAppListener = addListener.withTypes<RootState, AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()
export const createAppSelector = createSelector.withTypes<RootState>()
export const createAppAsyncThunk = createAsyncThunk.withTypes<{
  state: RootState
  dispatch: AppDispatch
}>()

export interface BasicResponse<T> {
  data: T
  message: string
}

export interface ExtraOptions {
  errorToast?: boolean
}

export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, ExtraOptions, FetchBaseQueryMeta> = async (args, api, extraOptions) => {
  const result = await fetchBaseQuery({
    baseUrl: getBaseUrl(),
    prepareHeaders: (headers) => {
      headers.set('Authorization', `Bearer ${window.localStorage.getItem(TOKEN)}`)
      return headers
    },
  })(args, api, extraOptions)
  const { error, data } = result
  if (error) {
    if (extraOptions?.errorToast !== false) {
      toast.error(error.data
        && typeof error.data === 'object'
        && 'message' in error.data
        && typeof error.data.message === 'string' ? error.data.message : 'System error, please try again later')
    }
    if (error.status === 401 || error.status === 403) {
      api.dispatch(setToken(undefined))
      window.localStorage.removeItem(TOKEN)
      void disconnect(getWagmiConfig(getEnv(location.hostname)))
      // window.location.href = '/'
    }
    return result
  }
  return {
    ...result,
    data: (data as BasicResponse<unknown>)?.data || null,
  }
}
